import React, { useEffect, useState } from 'react';
import DashboardOverview from '../../components/dashboard/DashboardOverview';
import { useAuthenticatedUser } from '../../contexts/AuthenticatedUserInfoContext';
import { useDisclosure, useToast } from '@chakra-ui/react';
import TaskModal from '../../components/modals/TaskBuiltInFormModal';
import ProjectModal from '../../components/ProjectManagement/modals/ProjectModal';
import ClientModal from '../../components/modals/ClientFormModal';
import { useCreateTask } from '../../hooks/useTasks';
import { useCreateProject } from '../../hooks/useProjects';
import { useClients } from '../../hooks/useClients';
import { CreateTaskData } from '../../api/taskApi';
import { CreateProjectData } from '../../api/projectApi';
import { CreateClientData } from '../../api/clientApi';
import { useOrganizationEnums } from '../../hooks/useOrganizationEnums';

const OverviewPage = () => {
  const { authenticatedUser } = useAuthenticatedUser();
  const { data: organizationEnums } = useOrganizationEnums();
  const toast = useToast();

  // Modal states
  const {
    isOpen: isTaskModalOpen,
    onOpen: onTaskModalOpen,
    onClose: onTaskModalClose,
  } = useDisclosure();
  const {
    isOpen: isProjectModalOpen,
    onOpen: onProjectModalOpen,
    onClose: onProjectModalClose,
  } = useDisclosure();
  const {
    isOpen: isClientModalOpen,
    onOpen: onClientModalOpen,
    onClose: onClientModalClose,
  } = useDisclosure();

  // Form states
  const [selectedProject, setSelectedProject] = useState<any | null>(null);
  const [taskFormData, setTaskFormData] = useState<CreateTaskData>({
    taskTitle: '',
    priority: 'medium',
    description: '',
    type: 'development',
    project: '',
    status: 'In progress',
    authBy: [],
    quotedPrice: undefined,
    dateStarted: undefined,
    dueDate: undefined,
  });
  const [projectFormData, setProjectFormData] = useState<CreateProjectData>({
    userWhoCreatedProject: authenticatedUser?.LoggedInUserId || '',
    projectName: '',
    type: 'software',
    client: '',
  });
  const [clientFormData, setClientFormData] = useState<CreateClientData>({
    clientName: '',
    additionalInformation: {
      leadPersonName: '',
      phone: '',
      email: '',
      secondaryContact: '',
      websiteUrl: '',
    },
  });

  // Mutations
  const createTaskMutation = useCreateTask();
  const createProjectMutation = useCreateProject();
  const { data: clients } = useClients();

  // Modal handlers
  const handleTaskModalClose = () => {
    setSelectedProject(null);
    onTaskModalClose();
  };

  // Form handlers
  const handleCreateTask = async () => {
    if (!taskFormData.description.trim()) {
      toast({
        title: 'Error',
        description: 'Description is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const taskDataWithProject = {
      ...taskFormData,
      project: selectedProject?._id || undefined,
    };
    try {
      await createTaskMutation.mutateAsync(taskDataWithProject);
      toast({
        title: 'Task Created',
        description: `Task has been created successfully!`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setTaskFormData({
        taskTitle: '',
        priority: 'medium',
        description: '',
        type: 'development',
        project: '',
        status: 'In progress',
        authBy: [],
        quotedPrice: undefined,
        dateStarted: undefined,
        dueDate: undefined,
      });
      setSelectedProject(null);
      onTaskModalClose();
    } catch (error: any) {
      let errorMsg = 'Failed to create task. Please try again.';
      if (error?.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      toast({
        title: 'Error',
        description: errorMsg,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      // Optionally log the full error for debugging
      // eslint-disable-next-line no-console
      console.error('Task creation error:', error?.response?.data || error);
    }
  };

  const handleCreateProject = async () => {
    if (!projectFormData.projectName.trim()) {
      toast({
        title: 'Error',
        description: 'Project name is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      const projectData = {
        ...projectFormData,
        userWhoCreatedProject: authenticatedUser?.LoggedInUserId || '',
      };
      await createProjectMutation.mutateAsync(projectData);
      toast({
        title: 'Success',
        description: 'Project created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setProjectFormData({
        userWhoCreatedProject: authenticatedUser?.LoggedInUserId || '',
        projectName: '',
        type: 'software',
        client: '',
      });
      onProjectModalClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create project',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <DashboardOverview
        firstName={authenticatedUser?.firstName}
        onCreateTask={onTaskModalOpen}
        onCreateProject={onProjectModalOpen}
        onCreateClient={onClientModalOpen}
      />
      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={handleTaskModalClose}
        formData={taskFormData}
        onFormChange={data => setTaskFormData({ ...taskFormData, ...data })}
        onSubmit={handleCreateTask}
        isLoading={createTaskMutation.isPending}
        selectedProject={selectedProject}
        onProjectChange={setSelectedProject}
      />
      <ProjectModal
        isOpen={isProjectModalOpen}
        onClose={onProjectModalClose}
        formData={projectFormData}
        setFormData={setProjectFormData}
        onSave={handleCreateProject}
        isLoading={createProjectMutation.isPending}
        clients={clients || []}
        editingProject={null}
        projectTypes={organizationEnums?.projectTypes ?? []}
      />
      <ClientModal
        isOpen={isClientModalOpen}
        onClose={onClientModalClose}
        formData={clientFormData}
        onFormChange={setClientFormData}
      />
    </>
  );
};

export default OverviewPage;
